import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  APP_BUILDER_BUILD_TAB_PATH,
  CRM_CLIENTS_PAGE,
  CUSTOMIZATION_PAGE,
} from 'src/constants';
import { ONBOARDING_CALL_SCHEDULER_URL } from 'src/constants/externalUrls';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import {
  useGetOnboardingTaskStatusQuery,
  useMarkOnboardingSurfaceSeenMutation,
  useUpdateStudioTaskStatusMutation,
} from 'src/services/api/improvedOnboardingApi';
import { setUnlockCardShowing } from 'src/store/ui/actions';
import { selectIsUnlockCardShowing } from 'src/store/ui/selectors';

import {
  ActivationChecklistCard,
  type ActivationChecklistStep,
} from './ActivationChecklistCard';
import {
  type ActivationStage,
  type ActivationStepId,
  APP_FIRST_CHECKLIST_ONE_STEP_IDS,
  areStepsCompleted,
  CHECKLIST_ONE_STEP_IDS,
  CHECKLIST_TWO_STEP_IDS,
  getActivationStage,
  getActivationStepCompletion,
  GUIDED_TOUR_ANOTHER_APP,
  GUIDED_TOUR_FIRST_CONTACT,
  isActivationStepInProgress,
} from './activationSteps';
import { PersonalizedGuidanceModal } from './PersonalizedGuidanceModal';
import {
  GUIDED_TOUR_PARAM,
  StudioChecklistTaskStatus,
} from './studioChecklist';
import { UnlockCard } from './UnlockCard';
import { useSkipActivationStep } from './useGuidedTour';
import { useIsAppFirstSignup } from './useIsAppFirstSignup';
import type { UseStudioChecklistResult } from './useStudioChecklist';

interface ActivationChecklistProps {
  checklist: UseStudioChecklistResult;
}

/**
 * The onboarding-activation Getting started card: checklist 1, the "You're on a
 * roll" card that unlocks checklist 2, then checklist 2 until it reads "You're
 * all set".
 */
export const ActivationChecklist = ({
  checklist: {
    onTaskClick,
    navigateAndCloseMobileSidebar,
    draftSessionPath,
    shouldRender,
  },
}: ActivationChecklistProps) => {
  const { t } = useTranslation();
  const { data: onboardingStatus } = useGetOnboardingTaskStatusQuery({});
  const [markSurfaceSeen] = useMarkOnboardingSurfaceSeenMutation();
  const [updateTaskStatus] = useUpdateStudioTaskStatusMutation();
  const skipStep = useSkipActivationStep();
  const [isGuidanceModalOpen, setIsGuidanceModalOpen] = useState(false);
  const checklistOneStepIds = useIsAppFirstSignup()
    ? APP_FIRST_CHECKLIST_ONE_STEP_IDS
    : CHECKLIST_ONE_STEP_IDS;
  // Collapse belongs to a stage, so each checklist opens expanded.
  const [collapsedStage, setCollapsedStage] = useState<ActivationStage | null>(
    null,
  );

  const seenAt = onboardingStatus?.fields.seenAt;
  const completion = useMemo(
    () => getActivationStepCompletion(onboardingStatus?.additionalFields.tasks),
    [onboardingStatus],
  );
  // The unlock card is a one-time surface, recorded when it shows rather than on
  // "Keep going", so a reload moves on to checklist 2. Recording it flips the
  // cached stage straight away, so the store keeps the card up for the session.
  const dispatch = useAppDispatch();
  const isUnlockShowing = useAppSelector(selectIsUnlockCardShowing);
  const loadedStage = getActivationStage(completion, seenAt);
  useEffect(() => {
    if (!shouldRender || loadedStage !== 'unlock') {
      return;
    }
    dispatch(setUnlockCardShowing(true));
    void markSurfaceSeen('part2Unlock');
  }, [shouldRender, loadedStage, markSurfaceSeen, dispatch]);
  const stage = isUnlockShowing ? 'unlock' : loadedStage;
  const isChecklistTwoComplete = areStepsCompleted(
    CHECKLIST_TWO_STEP_IDS,
    completion,
  );

  const toSteps = (
    stepIds: readonly ActivationStepId[],
  ): ActivationChecklistStep[] => {
    const labels: Record<ActivationStepId, string> = {
      explorePortal: t(
        ($) => $.page_apps.app_builder.getting_started.tasks.explore_portal,
      ),
      publishApp: t(
        ($) => $.page_apps.app_builder.getting_started.tasks.publish_app,
      ),
      branding: t(
        ($) => $.page_apps.app_builder.getting_started.tasks.add_branding,
      ),
      anotherApp: t(
        ($) => $.page_apps.app_builder.getting_started.tasks.add_another_app,
      ),
      contacts: t(
        ($) => $.page_apps.app_builder.getting_started.tasks.add_contacts,
      ),
      personalizedGuidance: t(
        ($) =>
          $.page_apps.app_builder.getting_started.tasks.personalized_guidance,
      ),
    };
    const actions: Record<ActivationStepId, () => void> = {
      explorePortal: () => onTaskClick('explorePortal'),
      publishApp: () => onTaskClick('publishApp'),
      branding: () => navigateAndCloseMobileSidebar(CUSTOMIZATION_PAGE.path),
      // Checklist 1 ends with a published app, so an unpublished one here is the
      // second app already under way. Otherwise, Add App with the template picker.
      anotherApp: () =>
        navigateAndCloseMobileSidebar(
          draftSessionPath ??
            `${APP_BUILDER_BUILD_TAB_PATH}&${GUIDED_TOUR_PARAM}=${GUIDED_TOUR_ANOTHER_APP}`,
        ),
      contacts: () =>
        navigateAndCloseMobileSidebar(
          `${CRM_CLIENTS_PAGE.path}?${GUIDED_TOUR_PARAM}=${GUIDED_TOUR_FIRST_CONTACT}`,
        ),
      personalizedGuidance: () => setIsGuidanceModalOpen(true),
    };
    return stepIds.map((id) => ({
      id,
      label: labels[id],
      isComplete: completion[id],
      isInProgress: isActivationStepInProgress(
        onboardingStatus?.additionalFields.tasks,
        id,
      ),
      onClick: actions[id],
    }));
  };

  // "Skip for now" checks the step off like every other "do this later"; the
  // close button just leaves it open.
  const skipGuidance = () => {
    setIsGuidanceModalOpen(false);
    void skipStep('personalizedGuidance');
  };

  // Opening the scheduler is the whole step: nothing reports back whether a call
  // was booked.
  const bookCall = () => {
    setIsGuidanceModalOpen(false);
    window.open(ONBOARDING_CALL_SCHEDULER_URL, '_blank', 'noopener,noreferrer');
    void updateTaskStatus({
      taskCode: 'personalizedGuidance',
      status: StudioChecklistTaskStatus.Done,
    });
  };

  const toggleCollapse = () =>
    setCollapsedStage((current) => (current === stage ? null : stage));

  const renderCard = () => {
    switch (stage) {
      case 'checklistOne':
        return (
          <ActivationChecklistCard
            title={t(($) => $.page_apps.app_builder.getting_started.title)}
            steps={toSteps(checklistOneStepIds)}
            isCollapsed={collapsedStage === stage}
            onToggleCollapse={toggleCollapse}
          />
        );
      case 'unlock':
        return (
          <UnlockCard
            onKeepGoing={() => dispatch(setUnlockCardShowing(false))}
          />
        );
      case 'checklistTwo':
        return (
          <ActivationChecklistCard
            title={
              isChecklistTwoComplete
                ? t(
                    ($) =>
                      $.page_apps.app_builder.getting_started.all_set_title,
                  )
                : t(
                    ($) =>
                      $.page_apps.app_builder.getting_started
                        .set_up_workspace_title,
                  )
            }
            steps={toSteps(CHECKLIST_TWO_STEP_IDS)}
            isCollapsed={collapsedStage === stage}
            onToggleCollapse={toggleCollapse}
          />
        );
    }
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      {renderCard()}
      <PersonalizedGuidanceModal
        isOpen={isGuidanceModalOpen}
        onClose={() => setIsGuidanceModalOpen(false)}
        onSkip={skipGuidance}
        onBookCall={bookCall}
      />
    </>
  );
};
